<template>
	<v-card tile elevation="0" style="height: 100%; overflow-y: hidden">
		<v-card-title class="text-left card-header fixed-card-title2">
			<v-row no-gutters>
				<v-col cols="6">
					<v-toolbar-title>{{ $t('edit-allergy-header') }}</v-toolbar-title>
				</v-col>
				<v-spacer />
				<v-icon color="white" @click="closeDrawer">mdi-close</v-icon>
			</v-row>
		</v-card-title>
		<v-card-text class="pt-4 pb-0 overflow-y-auto" style="height: calc(100vh - 221px)">
			<v-row dense class="ma-0">
				<v-col cols="12">
					<v-select
						v-model="selectedAllergen"
						:items="allergens"
						item-value="value"
						item-text="value"
						:label="$t('allergen')"
						outlined
						hide-details
						:menu-props="{ maxHeight: 199, offsetY: true }">
						<template v-slot:item="{ item }">
							<v-tooltip left>
								<template v-slot:activator="{ on, attrs }">
									<span v-bind="attrs" v-on="on" style="width: 100%">{{ item.value }}</span>
								</template>
								<span>{{ item.description }}</span>
							</v-tooltip>
						</template>
					</v-select>
				</v-col>
				<v-col cols="12">
					<v-select
						v-model="selectedReaction"
						:items="reactions"
						item-value="value"
						item-text="value"
						:label="$t('reaction')"
						outlined
						hide-details
						:menu-props="{ maxHeight: 199, offsetY: true }">
						<template v-slot:item="{ item }">
							<v-tooltip left>
								<template v-slot:activator="{ on, attrs }">
									<span v-bind="attrs" v-on="on" style="width: 100%">{{ item.value }}</span>
								</template>
								<span>{{ item.description }}</span>
							</v-tooltip>
						</template>
					</v-select>
				</v-col>
				<v-col cols="12">
					<v-select
						v-model="selectedSeverity"
						:items="severities"
						item-value="code"
						item-text="code"
						:label="$t('severity')"
						outlined
						hide-details
						:menu-props="{ maxHeight: 199, offsetY: true }">
						<template v-slot:item="{ item }">
							<v-tooltip left>
								<template v-slot:activator="{ on, attrs }">
									<span v-bind="attrs" v-on="on" style="width: 100%">{{ item.code }}</span>
								</template>
								<span>{{ item.description }}</span>
							</v-tooltip>
						</template>
					</v-select>
				</v-col>
				<v-col cols="12">
					<v-textarea v-model="notes" :label="$t('notes')" outlined dense no-resize hide-details rows="12" />
				</v-col>
			</v-row>
		</v-card-text>
		<v-container class="px-4">
			<v-row dense class="ma-0">
				<v-col cols="12">
					<InputTimeFormatter
						:inputValue="timeSpentSeconds"
						@update:inputValue="(val) => (timeSpentSeconds = val)"
						:inputLabel="$t('time-spent')"
						:dense="false" />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col cols="6" order="1">
					<v-btn @click="closeDrawer()" color="secondary" style="color: black" block>
						{{ $t('cancel') }}
					</v-btn>
				</v-col>
				<v-tooltip left v-if="!editButtonEnabled">
					<template v-slot:activator="{ on, attrs }">
						<v-col cols="6" v-bind="attrs" v-on="on" order="2">
							<v-btn disabled block>{{ $t('update') }}</v-btn>
						</v-col>
					</template>
					<span>{{ $t('fill-all-fields-warning') }}</span>
				</v-tooltip>
				<v-col cols="6" v-else order="2">
					<v-btn @click="editAllergy()" color="dark_purple" style="color: white" block>
						{{ $t('update') }}
					</v-btn>
				</v-col>
			</v-row>
		</v-container>
	</v-card>
</template>

<script>
import { mapState } from 'vuex';
import { getRowsForDataTable } from '@/utils/utilities';
import { mapActions } from 'vuex/dist/vuex.common.js';
import InputTimeFormatter from '../Client/components/InputTimeFormatter.vue';

export default {
	name: 'CCMAllergyEdit',

	data () {
		return {
			loading: false,
			additionalData: '',
			timeSpentSeconds: 0,
			severities: [],
			reactions: [],
			allergens: [],
			notes: '',
			selectedAllergen: '',
			selectedReaction: '',
			selectedSeverity: '',
		};
	},
	computed: {
		// Map state from Vuex
		...mapState({
			detailedUser: (state) => state.users.detailedUser,
			currentRecord: (state) => state.ccmRecords.ccmRecord,
			selectedAllergy: (state) => state.ccmAllergy.ccmAllergy,
			userData: (state) => state.authentication.userData,
		}),

		// Enable update button only if all fields are filled
		editButtonEnabled () {
			return this.notes.length && this.timeSpentSeconds && this.timeSpentSeconds <= 7200;
		},
	},

	watch: {
		// Whenever selectedAllergy updates, prefill form fields
		selectedAllergy () {
			this.medicalStaff = this.filterMedicalStaff();
			this.selectedAllergen = this.selectedAllergy?.allergen;
			this.selectedReaction = this.selectedAllergy?.reaction;
			this.selectedSeverity = this.selectedAllergy?.severity;
			this.notes = this.selectedAllergy?.notes;
		},
		deep: true,
	},

	async mounted () {
		this.medicalStaff = this.filterMedicalStaff();

		const responseSeverities = await this.getSeverities();
		this.severities = responseSeverities.data;

		const responseReactions = await this.getReactions();
		this.reactions = responseReactions.data;

		const responseAllergens = await this.getAllergens();
		this.allergens = responseAllergens.data;

		this.selectedAllergen = this.selectedAllergy?.allergen;
		this.selectedReaction = this.selectedAllergy?.reaction;
		this.selectedSeverity = this.selectedAllergy?.severity;
		this.notes = this.selectedAllergy?.notes;

		this.loadingStatus = false;
	},

	methods: {
		...mapActions({
			getObservationCategoryOptionsTemplate: 'ccmRecords/getObservationCategoryOptionsTemplate',
			getDiagnosisByPatientId: 'ccmDiagnoses/getCCMDiagnosesByPatientId',
			getSeverities: 'ccmRecords/getConditionSeverityOption',
			getReactions: 'ccmRecords/getConditionReactionOption',
			getAllergens: 'ccmRecords/getConditionAllergenOption',
		}),
		getRowsForDataTable,
		/**
		 * Resets local state and closes the details drawer.
		 */
		closeDrawer () {
			this.timeSpentSeconds = 0;
			this.additionalData = '';
			this.$store.dispatch('prescriptions/clearSearchedPrescriptions');
			this.$store.commit('SET_DETAILS_DRAWER', false);
			this.$store.commit('SET_UPDATE_DETAILS_DRAWER', false);
			this.$store.commit('SET_EDIT_DETAILS_DRAWER', false);
		},

		/**
		 * Constructs the updated allergy payload and dispatches Vuex actions
		 * to update the backend and refresh the patient allergy list.
		 */
		async editAllergy () {
			const body = {
				ccmRecordId: this.currentRecord.id,
				patientId: this.currentRecord.patientId,
				hospitalId: this.currentRecord.hospitalId,
				allergen: this.selectedAllergen,
				reaction: this.selectedReaction,
				severity: this.selectedSeverity,
				recordedAt: new Date().toISOString(),
				notes: this.notes,
				staffId: this.userData.id,
				timeSpentSeconds: this.timeSpentSeconds,
				additionalData: '',
			};

			try {
				const payloadBody = {
					id: this.selectedAllergy.id,
					body: body,
				};

				await this.$store.dispatch('ccmAllergy/updateCCMAllergy', payloadBody);
				await this.$store.dispatch('ccmAllergy/getCCMAllergyByPatientId', this.currentRecord.patientId);
			} catch (error) {
				this.$store.commit('alerts/add', {
					id: 'editedObservation',
					type: 'error',
					color: 'main_red',
					message: this.$t('failed!'),
				});
			} finally {
				this.closeDrawer();
			}
		},

		/**
		 * Filters medical team members to only include staff with roleId 3 or 4 and isSequoiaUser set.
		 * Useful for assigning relevant medical staff in dropdowns or logs.
		 */
		filterMedicalStaff () {
			return this.detailedUser.userData.medicalTeamsData[0].membersList
				.filter((member) => (member.roleId === 3 || member.roleId === 4) && member.isSequoiaUser && member.status === 1)
				.map((member) => ({
					...member,
					fullName: `${member.firstName} ${member.lastName}`,
				}));
		},
	},
	components: { InputTimeFormatter },
};
</script>
